export interface CachedData<T> {
    data: T;
    expiresAt: number;
}

export interface CacheConfig {
    enabled: boolean;
    duration: number;   // in milliseconds
    type: CacheType;
}

// Cache type enum
export enum CacheType {
    SESSION = 'session',    // In-memory, lost on app close
    PERSISTENT = 'persistent', // localStorage, survives refresh
    WEBVIEW_SESSION = 'webview_session' // In-memory, survives tab switches but not app close
}

// Cache configuration constants
export const SECOND = 1000;  // 1 second in milliseconds
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

// Special duration value for never-expiring cache
export const NEVER_EXPIRE = -1;

// In-memory session cache
class SessionCache {
    private cache = new Map<string, CachedData<any>>();
    
    get<T>(key: string): CachedData<T> | null {
        const cached = this.cache.get(key);
        if (!cached) return null;
        
        // Check if expired (except for NEVER_EXPIRE)
        if (cached.expiresAt !== NEVER_EXPIRE && Date.now() >= cached.expiresAt) {
            this.cache.delete(key);
            return null;
        }
        
        return cached;
    }
    
    set<T>(key: string, data: T, duration: number = DAY): void {
        const now = Date.now();
        const cacheData: CachedData<T> = {
            data,
            expiresAt: duration === NEVER_EXPIRE ? NEVER_EXPIRE : now + duration,
        };
        this.cache.set(key, cacheData);
    }
    
    clear(): void {
        this.cache.clear();
    }
    
    delete(key: string): void {
        this.cache.delete(key);
    }
    
    isValid(key: string): boolean {
        const cached = this.cache.get(key);
        if (!cached) return false;
        return cached.expiresAt === NEVER_EXPIRE || Date.now() < cached.expiresAt;
    }
    
    // Get all keys for iteration
    getKeys(): string[] {
        return Array.from(this.cache.keys());
    }
}

// Global session cache instance
export const sessionCache = new SessionCache();

// Cache configuration for specific URLs with their rules
export const CACHEABLE_URLS = {
    // GET endpoints
    // '/webclock/locations_list': {
    //     enabled: true,
    //     duration: NEVER_EXPIRE, // Cache for 12 hours
    //     type: CacheType.PERSISTENT, // Use persistent cache
    // },
    // '/webclock/department_list': {
    //     enabled: true,
    //     duration: NEVER_EXPIRE,  // Never expires unless forceRefresh
    //     type: CacheType.PERSISTENT, // Use persistent cache
    // },
    // '/webclock/work_codes_list': {
    //     enabled: true,
    //     duration: NEVER_EXPIRE,  // Never expires unless forceRefresh
    //     type: CacheType.PERSISTENT, // Use persistent cache
    // },
    // '/timeoff/timeoff_category_list': {
    //     enabled: true,
    //     duration: 12 * HOUR,  // Cache search results for 1 hour
    //     type: CacheType.SESSION, // Use session-only cache
    // },

    // POST endpoints that fetch data
    // '/attendance/attendance_tags': {
    //     enabled: true,
    //     duration: NEVER_EXPIRE,  // Never expires unless forceRefresh
    //     type: CacheType.PERSISTENT, // Use persistent cache
    // },
    // '/attendance/online': {
    //     enabled: true,
    //     duration: 300 * SECOND,  // Cache filtered results for 30 minutes
    //     type: CacheType.SESSION, // Use session-only cache
    // },
    // '/timeoff/fetch_timeoff_balance': {
    //     enabled: true,
    //     duration: 1800 * SECOND,  // Cache filtered results for 30 minutes
    //     type: CacheType.SESSION, // Use session-only cache
    // },
    // '/account/index': {
    //     enabled: true,
    //     duration: NEVER_EXPIRE,  // Cache for entire session
    //     type: CacheType.WEBVIEW_SESSION, // Use webview session cache
    // },
} as Record<string, CacheConfig>;

// Default cache configuration
export const DEFAULT_CACHE_CONFIG = {
    enabled: false,
    duration: DAY,
    type: CacheType.PERSISTENT, // Default to persistent
};

export const getCacheKey = (url: string, params: any): string => {
    return `api_cache_${url}_${JSON.stringify(params)}`;
};

// Get cache type for a specific URL
export const getCacheType = (url: string): CacheType => {
    const config = CACHEABLE_URLS[url as keyof typeof CACHEABLE_URLS];
    return config?.type || DEFAULT_CACHE_CONFIG.type;
};

export const getCachedData = <T>(key: string, cacheType?: CacheType): CachedData<T> | null => {
    const type = cacheType || CacheType.PERSISTENT;
    
    if (type === CacheType.SESSION || type === CacheType.WEBVIEW_SESSION) {
        return sessionCache.get<T>(key);
    } else {
        // Persistent cache (localStorage)
        try {
            const item = localStorage.getItem(key);
            if (!item) return null;
            
            const cached = JSON.parse(item) as CachedData<T>;
            
            // Never expire case
            if (cached.expiresAt === NEVER_EXPIRE) {
                return cached;
            }

            const now = Date.now();
            // Check if cache has expired
            if (now >= cached.expiresAt) {
                localStorage.removeItem(key);
                return null;
            }
            
            return cached;
        } catch {
            return null;
        }
    }
};

export const setCachedData = <T>(key: string, data: T, duration: number = DAY, cacheType?: CacheType): void => {
    const type = cacheType || CacheType.PERSISTENT;
    
    if (type === CacheType.SESSION || type === CacheType.WEBVIEW_SESSION) {
        sessionCache.set<T>(key, data, duration);
    } else {
        // Persistent cache (localStorage)
        const now = Date.now();
        const cacheData: CachedData<T> = {
            data,
            expiresAt: duration === NEVER_EXPIRE ? NEVER_EXPIRE : now + duration,
        };
        localStorage.setItem(key, JSON.stringify(cacheData));
    }
};

export const isCacheValid = (key: string, cacheType?: CacheType): boolean => {
    const type = cacheType || CacheType.PERSISTENT;
    
    if (type === CacheType.SESSION || type === CacheType.WEBVIEW_SESSION) {
        return sessionCache.isValid(key);
    } else {
        // Persistent cache (localStorage)
        try {
            const item = localStorage.getItem(key);
            if (!item) return false;
            
            const cached = JSON.parse(item) as CachedData<any>;
            // If expiresAt is NEVER_EXPIRE, cache is always valid
            return cached.expiresAt === NEVER_EXPIRE || Date.now() < cached.expiresAt;
        } catch {
            return false;
        }
    }
};

export const clearCache = (keyPrefix: string = 'api_cache_', cacheType?: CacheType): void => {
    const type = cacheType || CacheType.PERSISTENT;
    
    if (type === CacheType.SESSION || type === CacheType.WEBVIEW_SESSION) {
        // Clear session cache entries that match the prefix
        const keys = sessionCache.getKeys();
        for (const key of keys) {
            if (key.startsWith(keyPrefix)) {
                sessionCache.delete(key);
            }
        }
    } else {
        // Clear localStorage cache
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(keyPrefix)) {
                localStorage.removeItem(key);
            }
        }
    }
};

// Clear all caches (both session and persistent)
export const clearAllCaches = (keyPrefix: string = 'api_cache_'): void => {
    clearCache(keyPrefix, CacheType.SESSION);
    clearCache(keyPrefix, CacheType.PERSISTENT);
};

// Helper function to get cached data with automatic cache type detection
export const getCachedDataForUrl = <T>(url: string, params: any): CachedData<T> | null => {
    const key = getCacheKey(url, params);
    const cacheType = getCacheType(url);
    return getCachedData<T>(key, cacheType);
};

// Helper function to set cached data with automatic cache type detection
export const setCachedDataForUrl = <T>(url: string, params: any, data: T, duration?: number): void => {
    const key = getCacheKey(url, params);
    const cacheType = getCacheType(url);
    const cacheDuration = duration || CACHEABLE_URLS[url as keyof typeof CACHEABLE_URLS]?.duration || DEFAULT_CACHE_CONFIG.duration;
    setCachedData<T>(key, data, cacheDuration, cacheType);
};
